import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Tag, Trash2, Save, X, GripVertical, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAllArticleCategories, useCreateArticleCategory, useUpdateArticleCategory, useDeleteArticleCategory } from '../../hooks/useSupabase'
import type { Database } from '../../lib/supabase'

type ArticleCategory = Database['public']['Tables']['article_categories']['Row']

// Predefined colors for easy selection
const colorOptions = [
  { value: '#10B981', name: 'Grön' },
  { value: '#3B82F6', name: 'Blå' },
  { value: '#F59E0B', name: 'Orange' },
  { value: '#8B5CF6', name: 'Lila' },
  { value: '#06B6D4', name: 'Cyan' },
  { value: '#84CC16', name: 'Lime' },
  { value: '#EF4444', name: 'Röd' },
  { value: '#EC4899', name: 'Rosa' },
  { value: '#6B7280', name: 'Grå' },
  { value: '#F97316', name: 'Mörkorange' },
]

export function ArticleCategories() {
  const navigate = useNavigate()
  const { data: categories = [], isLoading } = useAllArticleCategories()
  const createCategory = useCreateArticleCategory()
  const updateCategory = useUpdateArticleCategory()
  const deleteCategory = useDeleteArticleCategory()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ name: '', description: '', color: '#10B981', is_active: true })
  const [isCreating, setIsCreating] = useState(false)
  const [newForm, setNewForm] = useState({ name: '', description: '', color: '#10B981' })

  const handleStartEdit = (category: ArticleCategory) => {
    setEditingId(category.id)
    setEditForm({
      name: category.name,
      description: category.description || '',
      color: category.color,
      is_active: category.is_active
    })
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditForm({ name: '', description: '', color: '#10B981', is_active: true })
  }

  const handleSaveEdit = async () => {
    if (!editingId || !editForm.name.trim()) {
      toast.error('Namn är obligatoriskt')
      return
    }

    try {
      await updateCategory.mutateAsync({
        id: editingId,
        updates: {
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          color: editForm.color,
          is_active: editForm.is_active
        }
      })
      toast.success('Kategori uppdaterad')
      setEditingId(null)
    } catch (error) {
      console.error('Error updating category:', error)
      toast.error('Kunde inte uppdatera kategori')
    }
  }

  const handleCreate = async () => {
    if (!newForm.name.trim()) {
      toast.error('Namn är obligatoriskt')
      return
    }

    try {
      const maxOrder = categories.reduce((max, c) => Math.max(max, c.sort_order), 0)
      await createCategory.mutateAsync({
        name: newForm.name.trim(),
        description: newForm.description.trim() || null,
        color: newForm.color,
        sort_order: maxOrder + 1
      })
      toast.success('Kategori skapad')
      setIsCreating(false)
      setNewForm({ name: '', description: '', color: '#10B981' })
    } catch (error) {
      console.error('Error creating category:', error)
      toast.error('Kunde inte skapa kategori')
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Är du säker på att du vill ta bort kategorin "${name}"? Artiklar med denna kategori kommer inte längre ha en kategori.`)) {
      return
    }

    try {
      await deleteCategory.mutateAsync(id)
      toast.success('Kategori borttagen')
    } catch (error) {
      console.error('Error deleting category:', error)
      toast.error('Kunde inte ta bort kategori')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar kategorier...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/articles')}
            className="p-2 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-nature-600 dark:text-nature-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">Artikelkategorier</h1>
            <p className="text-sm text-nature-600 dark:text-nature-400 mt-1">
              Hantera kategorier för artiklar
            </p>
          </div>
        </div>
        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Lägg till</span>
          </button>
        )}
      </div>

      {/* Create new category form */}
      {isCreating && (
        <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 mb-4">Ny kategori</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Namn *</label>
              <input
                type="text"
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                className="input-field"
                placeholder="Kategorinamn"
              />
            </div>
            <div>
              <label className="form-label">Beskrivning</label>
              <input
                type="text"
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                className="input-field"
                placeholder="Valfri beskrivning"
              />
            </div>
            <div>
              <label className="form-label">Färg</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newForm.color}
                  onChange={(e) => setNewForm({ ...newForm, color: e.target.value })}
                  className="h-10 w-14 rounded border border-nature-300 dark:border-nature-600 cursor-pointer"
                />
                <div className="flex flex-wrap gap-1">
                  {colorOptions.slice(0, 6).map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setNewForm({ ...newForm, color: color.value })}
                      className={`w-6 h-6 rounded-full border-2 ${newForm.color === color.value ? 'border-nature-900 dark:border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => { setIsCreating(false); setNewForm({ name: '', description: '', color: '#10B981' }) }}
              className="btn-secondary flex items-center gap-1"
            >
              <X className="h-4 w-4" />
              Avbryt
            </button>
            <button
              onClick={handleCreate}
              disabled={createCategory.isPending}
              className="btn-primary flex items-center gap-1"
            >
              <Save className="h-4 w-4" />
              {createCategory.isPending ? 'Sparar...' : 'Spara'}
            </button>
          </div>
        </div>
      )}

      {/* Categories list */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
        <div className="divide-y divide-nature-200 dark:divide-nature-700">
          {categories.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="h-12 w-12 text-nature-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-nature-900 dark:text-nature-100 mb-2">
                Inga kategorier än
              </h3>
              <p className="text-nature-600 dark:text-nature-400">
                Lägg till din första kategori för att komma igång
              </p>
            </div>
          ) : (
            categories.map((category) => (
              <div
                key={category.id}
                className="p-4 hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors"
              >
                {editingId === category.id ? (
                  // Edit mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="form-label">Namn *</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="form-label">Beskrivning</label>
                        <input
                          type="text"
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="form-label">Färg</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={editForm.color}
                            onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                            className="h-10 w-14 rounded border border-nature-300 dark:border-nature-600 cursor-pointer"
                          />
                          <div className="flex flex-wrap gap-1">
                            {colorOptions.slice(0, 5).map((color) => (
                              <button
                                key={color.value}
                                onClick={() => setEditForm({ ...editForm, color: color.value })}
                                className={`w-6 h-6 rounded-full border-2 ${editForm.color === color.value ? 'border-nature-900 dark:border-white' : 'border-transparent'}`}
                                style={{ backgroundColor: color.value }}
                                title={color.name}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Status</label>
                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editForm.is_active}
                            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                            className="h-4 w-4 text-primary-600 rounded border-nature-300"
                          />
                          <span className={`text-sm font-medium ${editForm.is_active ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {editForm.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={handleCancelEdit}
                        className="btn-secondary flex items-center gap-1"
                      >
                        <X className="h-4 w-4" />
                        Avbryt
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={updateCategory.isPending}
                        className="btn-primary flex items-center gap-1"
                      >
                        <Save className="h-4 w-4" />
                        {updateCategory.isPending ? 'Sparar...' : 'Spara'}
                      </button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <GripVertical className="h-5 w-5 text-nature-400 cursor-grab" />
                      <div
                        className="w-8 h-8 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-nature-900 dark:text-nature-100">
                            {category.name}
                          </span>
                          {!category.is_active && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200">
                              Inaktiv
                            </span>
                          )}
                        </div>
                        {category.description && (
                          <p className="text-sm text-nature-500 dark:text-nature-400">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleStartEdit(category)}
                        className="px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors"
                      >
                        Redigera
                      </button>
                      <button
                        onClick={() => handleDelete(category.id, category.name)}
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
